/* Bhairav Tatva — gallery, gemstone cards, collection filters, hero slides. No dependencies. */
(() => {
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const saveData = !!(navigator.connection && navigator.connection.saveData);

  const vimeoSrc = (id) =>
    `https://player.vimeo.com/video/${encodeURIComponent(id)}?background=1&autoplay=1&loop=1&muted=1&playsinline=1`;

  /* Vimeo frames report their state on the host element (data-vstate = loading | playing | error),
     so cards and the gallery can show a spinner while the clip loads and a retry if it fails.
     The frame only fades in once Vimeo says it is actually playing. */
  const frames = new Map();
  const post = (f, msg) => { try { f.contentWindow.postMessage(JSON.stringify(msg), 'https://player.vimeo.com'); } catch (e) {} };
  const setVState = (host, v) => {
    if (!host) return;
    if (v) host.dataset.vstate = v; else delete host.dataset.vstate;
    host.dispatchEvent(new CustomEvent('bt:vstate', { bubbles: true, detail: v }));
  };
  const listen = (f) => ['playing', 'timeupdate', 'error'].forEach((ev) => post(f, { method: 'addEventListener', value: ev }));
  function reveal(f) {
    const st = frames.get(f);
    if (!st || st.done) return;
    st.done = true;
    clearTimeout(st.fail); clearTimeout(st.fallback);
    f.classList.add('is-ready');
    setVState(st.host, 'playing');
  }
  function failFrame(f) {
    const st = frames.get(f);
    if (!st || st.done) return;
    clearTimeout(st.fail); clearTimeout(st.fallback);
    setVState(st.host, 'error');
    if (st.onError) st.onError(f);
  }
  // Called whenever a frame is expected to play now: (re)starts the "this is taking too long" timer.
  function expect(f) {
    const st = frames.get(f);
    if (!st || st.done) return;
    setVState(st.host, 'loading');
    clearTimeout(st.fail);
    // Background tabs don't start video, so only give up while the page is actually visible.
    const check = () => { if (document.hidden) { st.fail = setTimeout(check, 5000); return; } failFrame(f); };
    st.fail = setTimeout(check, 15000);
  }
  function dropFrame(f) {
    const st = frames.get(f);
    if (st) { clearTimeout(st.fail); clearTimeout(st.fallback); frames.delete(f); }
    f.remove();
  }
  function vimeoFrame(id, host, opts = {}) {
    const f = document.createElement('iframe');
    f.src = vimeoSrc(id);
    f.className = 'bt-vframe';
    f.allow = 'autoplay; picture-in-picture';
    f.title = 'Gemstone video';
    f.setAttribute('tabindex', '-1');
    const st = { host, api: false, done: false, onError: opts.onError, paused: !!opts.paused };
    frames.set(f, st);
    f.addEventListener('load', () => {
      st.loaded = true;
      listen(f);
      // Older players that never answer the API: show the frame shortly after it loads, as before.
      st.fallback = setTimeout(() => { if (!st.api && !st.paused) reveal(f); }, 4000);
    });
    if (!opts.paused) expect(f); else setVState(host, 'loading');
    return f;
  }
  window.addEventListener('message', (e) => {
    if (e.origin !== 'https://player.vimeo.com') return;
    let d = e.data;
    if (typeof d === 'string') { try { d = JSON.parse(d); } catch (err) { return; } }
    if (!d || typeof d !== 'object') return;
    for (const [f, st] of frames) {
      if (f.contentWindow !== e.source) continue;
      st.api = true;
      if (d.event === 'ready') { listen(f); if (st.paused) post(f, { method: 'pause' }); }
      else if (d.event === 'playing' || (d.event === 'timeupdate' && d.data && d.data.seconds > 0)) { if (!st.paused) reveal(f); }
      else if (d.event === 'error') failFrame(f);
      break;
    }
  });
  const vimeoCmd = (frame, method) => {
    const st = frames.get(frame);
    if (st) {
      st.paused = method === 'pause';
      if (method === 'play' && !st.done) {
        if (st.loaded && !st.api) reveal(frame); else expect(frame);
      }
    }
    post(frame, { method });
  };

  /* ---------------- Product gallery ---------------- */
  class BtGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.bt-gal__track');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.thumbs = Array.from(this.querySelectorAll('.bt-gal__thumb'));
      this.dots = Array.from(this.querySelectorAll('.bt-gal__dot'));
      this.prev = this.querySelector('.bt-gal__arrow--prev');
      this.next = this.querySelector('.bt-gal__arrow--next');
      this.i = -1;

      this.querySelectorAll('[data-dir]').forEach((b) =>
        b.addEventListener('click', () => this.go(this.i + Number(b.dataset.dir)))
      );
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.go))));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.i + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.i - 1); }
      });

      const io = new IntersectionObserver(
        (entries) => entries.forEach((e) => {
          if (e.isIntersecting && e.intersectionRatio >= 0.6) this.setActive(Number(e.target.dataset.index));
        }),
        { root: this.track, threshold: [0.6] }
      );
      this.slides.forEach((s) => io.observe(s));
      this.setActive(0);

      // Hover zoom on desktop
      if (fine.matches) {
        this.querySelectorAll('.bt-gal__zoom').forEach((z) => {
          z.addEventListener('mousemove', (e) => {
            const r = z.getBoundingClientRect();
            z.style.setProperty('--zx', `${((e.clientX - r.left) / r.width) * 100}%`);
            z.style.setProperty('--zy', `${((e.clientY - r.top) / r.height) * 100}%`);
            z.classList.add('is-zoom');
          });
          z.addEventListener('mouseleave', () => z.classList.remove('is-zoom'));
        });
      }

      // Pause video when the gallery scrolls out of view
      new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        this.syncMedia();
      }).observe(this);

      const warm = () => setTimeout(() => this.prewarm(), 1200);
      if (document.readyState === 'complete') warm(); else window.addEventListener('load', warm, { once: true });
    }

    makeFrame(s, paused = false) {
      return vimeoFrame(s.dataset.vimeo, s, {
        paused,
        onError: () => {
          if (s.querySelector('.bt-vretry')) return;
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'bt-vretry';
          b.innerHTML = '<span>The video didn’t load</span><strong>Try again</strong>';
          b.addEventListener('click', () => {
            b.remove();
            const old = s.querySelector('.bt-vframe');
            if (old) dropFrame(old);
            const f = this.makeFrame(s);
            s.appendChild(f);
          });
          s.appendChild(b);
        },
      });
    }

    // Desktop: load the clip quietly after the page settles, so it is ready when the shopper gets to it.
    prewarm() {
      if (!fine.matches || saveData) return;
      const s = this.slides.find((x) => x.dataset.vimeo);
      if (!s || s.querySelector('.bt-vframe')) return;
      const f = this.makeFrame(s, true);
      s.appendChild(f);
      s.classList.add('is-playing');
      this.syncMedia();
    }

    go(n) {
      const max = this.slides.length - 1;
      n = Math.max(0, Math.min(max, n));
      const s = this.slides[n];
      this.track.scrollTo({ left: s.offsetLeft - this.track.offsetLeft, behavior: reduced.matches ? 'auto' : 'smooth' });
      this.setActive(n);
    }

    setActive(n) {
      if (n === this.i) return;
      this.i = n;
      this.thumbs.forEach((t) => t.classList.toggle('is-active', Number(t.dataset.go) === n));
      this.dots.forEach((d, k) => d.classList.toggle('is-active', k === n));
      if (this.prev) this.prev.disabled = n === 0;
      if (this.next) this.next.disabled = n === this.slides.length - 1;
      this.syncMedia();
    }

    syncMedia() {
      this.slides.forEach((s, k) => {
        const active = k === this.i && this.visible !== false;
        if (s.dataset.vimeo) {
          s.classList.toggle('is-current', active);
          let f = s.querySelector('.bt-vframe');
          if (active && !f) { f = this.makeFrame(s); s.appendChild(f); s.classList.add('is-playing'); }
          else if (f) vimeoCmd(f, active ? 'play' : 'pause');
        }
        const v = s.querySelector('video');
        if (v) { if (active) v.play().catch(() => {}); else v.pause(); }
      });
    }
  }
  if (!customElements.get('bt-gallery')) customElements.define('bt-gallery', BtGallery);

  /* ---------------- Gemstone cards: video preview ---------------- */
  let current = null;
  let hoverTimer = null;
  function playCard(m) {
    if (current === m) return;
    stopCard();
    current = m;
    m.appendChild(vimeoFrame(m.dataset.vimeo, m, {
      onError: (f) => {
        dropFrame(f);
        m.classList.remove('is-playing');
        m.removeAttribute('data-pinned');
        if (current === m) current = null;
      },
    }));
    m.classList.add('is-playing');
  }
  function stopCard() {
    if (!current) return;
    const f = current.querySelector('.bt-vframe');
    if (f) dropFrame(f);
    current.classList.remove('is-playing');
    setVState(current, null);
    current = null;
  }
  document.addEventListener('pointerover', (e) => {
    if (!fine.matches || !e.target.closest) return;
    const m = e.target.closest('.bt-gcard__media[data-vimeo]');
    if (!m) return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(() => playCard(m), 120);
  });
  document.addEventListener('pointerout', (e) => {
    if (!e.target.closest) return;
    const m = e.target.closest('.bt-gcard__media[data-vimeo]');
    if (m && !m.contains(e.relatedTarget)) {
      clearTimeout(hoverTimer);
      if (current === m) stopCard();
    }
  });

  // Shared with bt-v3.js: the Video button on cards plays the clip in place.
  window.btCards = { play: playCard, stop: stopCard, current: () => current };

  /* ---------------- Collection filters ---------------- */
  const inrShort = (n) => {
    if (n >= 100000) return `₹${String(+(n / 100000).toFixed(2)).replace(/\.0+$/, '')} L`;
    return `₹${Math.round(n).toLocaleString('en-IN')}`;
  };
  const parseBuckets = (str) =>
    (str || '').split(',').map((s) => s.trim()).filter(Boolean).map((s) => {
      const [a, b] = s.split('-');
      return { key: s, min: parseFloat(a) || 0, max: b === undefined || b === '' ? Infinity : parseFloat(b) };
    });
  const rangeLabel = (g, b) => {
    const unit = g === 'carat' ? ' ct' : g === 'ratti' ? ' ratti' : '';
    const f = g === 'price' ? inrShort : (n) => String(n);
    if (b.min === 0 && b.max !== Infinity) return `Under ${f(b.max)}${unit}`;
    if (b.max === Infinity) return `${f(b.min)}${unit}+`;
    return g === 'price' ? `${f(b.min)} – ${f(b.max)}` : `${b.min} – ${b.max}${unit}`;
  };

  function initFilters(root) {
    const grid = root.querySelector('[data-fl-grid]');
    if (!grid) return;
    const pageSize = Number(root.dataset.pageSize) || 24;
    const items = Array.from(grid.querySelectorAll('.bt-gcard')).map((el) => ({
      el,
      price: Number(el.dataset.price) || 0,
      carat: Number(el.dataset.carat) || 0,
      ratti: Number(el.dataset.ratti) || 0,
      shape: el.dataset.shape || '',
      origin: el.dataset.origin || '',
      treatment: el.dataset.treatment || '',
      type: el.dataset.type || '',
      order: Number(el.dataset.order) || 0,
    }));
    if (!items.length) { root.classList.add('bt-gcol--empty'); return; }

    const buckets = { price: parseBuckets(root.dataset.price), carat: parseBuckets(root.dataset.carat), ratti: parseBuckets(root.dataset.ratti) };
    const groups = Array.from(root.querySelectorAll('.bt-fl__group')).map((fs) => ({ fs, name: fs.dataset.group, kind: fs.dataset.kind }));
    const state = {};
    groups.forEach((g) => (state[g.name] = new Set()));
    let sort = 'new';
    let shown = pageSize;

    const findBucket = (g, key) => buckets[g].find((b) => b.key === key);
    const match = (it, g, val) => {
      if (buckets[g]) { const b = findBucket(g, val); return !!b && it[g] > 0 && it[g] >= b.min && it[g] < b.max; }
      return it[g] === val;
    };
    const passes = (it, except) => groups.every((g) => {
      if (g.name === except || !state[g.name].size) return true;
      for (const v of state[g.name]) if (match(it, g.name, v)) return true;
      return false;
    });
    const labelFor = (g, v) => (buckets[g] ? rangeLabel(g, findBucket(g, v) || { min: 0, max: Infinity }) : v);

    // Build options once, from what is actually in this collection
    groups.forEach((g) => {
      let values;
      if (buckets[g.name]) values = buckets[g.name].filter((b) => items.some((it) => match(it, g.name, b.key))).map((b) => b.key);
      else values = Array.from(new Set(items.map((it) => it[g.name]).filter(Boolean))).sort((a, b) => a.localeCompare(b));
      g.values = values;
      if (values.length < 2) { g.fs.hidden = true; return; }
      const box = g.fs.querySelector('.bt-fl__opts');
      box.innerHTML = '';
      g.inputs = values.map((v) => {
        const lab = document.createElement('label');
        lab.className = 'bt-fl__opt';
        const inp = document.createElement('input');
        inp.type = 'checkbox';
        inp.value = v;
        inp.addEventListener('change', () => {
          if (inp.checked) state[g.name].add(v); else state[g.name].delete(v);
          shown = pageSize;
          apply();
        });
        const t = document.createElement('span');
        t.textContent = labelFor(g.name, v);
        const n = document.createElement('em');
        lab.append(inp, t, n);
        box.appendChild(lab);
        return { inp, n, lab, v };
      });
      g.fs.classList.toggle('bt-fl__group--chips', g.kind === 'range');
    });

    // Restore from URL
    const params = new URLSearchParams(location.search);
    groups.forEach((g) => {
      const raw = params.get(g.name);
      if (raw && g.values) raw.split(',').forEach((v) => { if (g.values.includes(v)) state[g.name].add(v); });
    });
    if (params.get('sort')) sort = params.get('sort');
    const sortSel = root.querySelector('[data-fl-sort]');
    if (sortSel) {
      sortSel.value = sort;
      sortSel.addEventListener('change', () => { sort = sortSel.value; apply(); });
    }

    const counts = root.querySelectorAll('[data-fl-count]');
    const badge = root.querySelector('[data-fl-badge]');
    const active = root.querySelector('[data-fl-active]');
    const empty = root.querySelector('[data-fl-empty]');
    const more = root.querySelector('[data-fl-more]');
    if (more) more.querySelector('button').addEventListener('click', () => { shown += pageSize; apply(false); });

    const sorters = {
      new: (a, b) => a.order - b.order,
      'price-asc': (a, b) => a.price - b.price,
      'price-desc': (a, b) => b.price - a.price,
      'carat-desc': (a, b) => b.carat - a.carat,
      'carat-asc': (a, b) => a.carat - b.carat,
    };

    function apply(updateUrl = true) {
      const list = items.filter((it) => passes(it)).sort(sorters[sort] || sorters.new);
      const set = new Set(list);
      const frag = document.createDocumentFragment();
      list.forEach((it, k) => { it.el.hidden = k >= shown; frag.appendChild(it.el); });
      items.forEach((it) => { if (!set.has(it)) { it.el.hidden = true; frag.appendChild(it.el); } });
      grid.appendChild(frag);

      counts.forEach((c) => (c.textContent = list.length));
      if (empty) empty.hidden = list.length > 0;
      if (more) more.hidden = list.length <= shown;

      let n = 0;
      active.innerHTML = '';
      groups.forEach((g) => {
        if (!g.inputs) return;
        g.inputs.forEach((o) => {
          const c = items.filter((it) => passes(it, g.name) && match(it, g.name, o.v)).length;
          o.n.textContent = c;
          o.inp.checked = state[g.name].has(o.v);
          o.lab.classList.toggle('is-off', c === 0 && !o.inp.checked);
          if (o.inp.checked) {
            n++;
            const pill = document.createElement('button');
            pill.type = 'button';
            pill.className = 'bt-pill';
            pill.innerHTML = '<span></span><svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 2l6 6M8 2 2 8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
            pill.firstChild.textContent = labelFor(g.name, o.v);
            pill.setAttribute('aria-label', `Remove ${labelFor(g.name, o.v)}`);
            pill.addEventListener('click', () => { state[g.name].delete(o.v); shown = pageSize; apply(); });
            active.appendChild(pill);
          }
        });
      });
      if (n) {
        const clr = document.createElement('button');
        clr.type = 'button';
        clr.className = 'bt-pill bt-pill--clear';
        clr.textContent = 'Clear all';
        clr.addEventListener('click', clearAll);
        active.appendChild(clr);
      }
      if (badge) { badge.hidden = !n; badge.textContent = n; }

      if (updateUrl) {
        const p = new URLSearchParams(location.search);
        groups.forEach((g) => { if (state[g.name].size) p.set(g.name, Array.from(state[g.name]).join(',')); else p.delete(g.name); });
        if (sort !== 'new') p.set('sort', sort); else p.delete('sort');
        const q = p.toString();
        history.replaceState(null, '', location.pathname + (q ? `?${q}` : ''));
      }
      document.dispatchEvent(new CustomEvent('bt:grid'));
    }
    function clearAll() { groups.forEach((g) => state[g.name].clear()); shown = pageSize; apply(); }
    root.querySelectorAll('[data-fl-clear]').forEach((b) => b.addEventListener('click', clearAll));

    const open = () => { root.classList.add('is-fl-open'); document.documentElement.classList.add('bt-lock'); };
    const close = () => { root.classList.remove('is-fl-open'); document.documentElement.classList.remove('bt-lock'); };
    root.querySelectorAll('[data-fl-open]').forEach((b) => b.addEventListener('click', open));
    root.querySelectorAll('[data-fl-close]').forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    apply(false);
    root.classList.add('is-ready');
  }

  /* ---------------- Hero slides ---------------- */
  class BtSlides extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.bt-slide'));
      this.tabs = Array.from(this.querySelectorAll('.bt-slides__tab'));
      if (this.slides.length < 2) return;
      this.i = 0;
      this.tabs.forEach((t, k) => t.addEventListener('click', () => this.go(k)));
      this.dur = parseFloat(getComputedStyle(this).getPropertyValue('--bt-dur')) || 4000;
      this.elapsed = 0;
      let last = performance.now();
      setInterval(() => {
        const now = performance.now();
        const running = !this.classList.contains('is-manual') && !document.hidden && !reduced.matches;
        if (running) {
          this.elapsed += now - last;
          if (this.elapsed >= this.dur) this.go(this.i + 1);
        }
        last = now;
      }, 200);
      // Keeps running while hovered or focused (Bharat: it shouldn't stop when the pointer is on it).
      document.addEventListener('visibilitychange', () => this.classList.toggle('is-hidden', document.hidden));

      let x0 = null;
      let y0 = null;
      this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        const dy = e.changedTouches[0].clientY - y0;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy)) this.go(this.i + (dx < 0 ? 1 : -1));
        x0 = null;
      }, { passive: true });
      this.querySelectorAll('[data-slide-dir]').forEach((b) => b.addEventListener('click', () => this.go(this.i + Number(b.dataset.slideDir))));
      this.classList.add('is-live');
    }
    go(n) {
      const len = this.slides.length;
      n = (n + len) % len;
      if (n === this.i) return;
      this.elapsed = 0;
      this.slides[this.i].classList.remove('is-active');
      this.slides[this.i].setAttribute('aria-hidden', 'true');
      this.slides[this.i].inert = true;
      this.i = n;
      this.slides[n].classList.add('is-active');
      this.slides[n].removeAttribute('aria-hidden');
      this.slides[n].inert = false;
      this.tabs.forEach((t, k) => {
        t.classList.toggle('is-active', k === n);
        t.setAttribute('aria-selected', k === n ? 'true' : 'false');
      });
    }
  }
  if (!customElements.get('bt-slides')) customElements.define('bt-slides', BtSlides);

  const boot = () => document.querySelectorAll('[data-bt-filters]:not(.is-ready)').forEach(initFilters);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
